import type { InfoPopupState } from '../shared/infoPopup/infoPopupState';
import type { ArrowDirection } from '../components/atomic/buttons/arrows/Arrows';
import {
  isDailyLoginRewardClaimedToday,
  isDailyLoginStreakCompleted,
  isNotificationsRewardClaimed,
  isQuizComplete,
  isQuizDailyLimitReached,
  isVkSubscribeRewardClaimed,
  type PlayerEconomyState,
} from '../../../domain';
import {
  TASK_INDEX_DAILY_LOGIN,
  TASK_INDEX_NOTIFICATIONS,
  TASK_INDEX_QUIZ,
  TASK_INDEX_VK_SUBSCRIBE,
} from '../../navigation/screenIds';
import taskName1Svg from './task_card/task_name_1.svg';
import taskName2Svg from './task_card/task_name_2.svg';
import taskName3Svg from './task_card/task_name_3.svg';
import taskName4Svg from './task_card/task_name_4.svg';
import rewardValue1Svg from './task_card/reward_value_1.svg';
import rewardValue2Svg from './task_card/reward_value_2.svg';
import rewardValue3Svg from './task_card/reward_value_3.svg';
import rewardValue4Svg from './task_card/reward_value_4.svg';

export type TaskListScreenProps = {
  playerEconomy: PlayerEconomyState;
  onNavigate?: (direction: ArrowDirection) => void;
  onTaskClick?: (taskIndex: number) => void;
  onInfoClick?: () => void;
  onGoBack?: () => void;
};

export type TicketsScreenState = InfoPopupState;

export type TaskCardModel = {
  top: number;
  taskNameSrc: string;
  taskNameAlt: string;
  taskNameLayout: { left: number; top: number; width: number; height: number };
  rewardValueSrc: string;
  rewardValueAlt: string;
  rewardValueLayout: { left: number; top: number; width: number; height: number };
  completed: boolean;
};

type TaskCardTemplate = Omit<TaskCardModel, 'completed'>;

export const TASK_TEMPLATES: readonly TaskCardTemplate[] = [
  {
    top: 740,
    taskNameSrc: taskName1Svg,
    taskNameAlt: 'Enable notifications',
    taskNameLayout: { left: 38.2, top: 68.529, width: 364, height: 44 },
    rewardValueSrc: rewardValue1Svg,
    rewardValueAlt: '+3',
    rewardValueLayout: { left: 545.296, top: 58.471, width: 59, height: 71 },
  },
  {
    top: 943,
    taskNameSrc: taskName2Svg,
    taskNameAlt: 'Daily login (1 day)',
    taskNameLayout: { left: 38.2, top: 65.73, width: 407, height: 46 },
    rewardValueSrc: rewardValue2Svg,
    rewardValueAlt: '+1',
    rewardValueLayout: { left: 548.731, top: 61.84, width: 51, height: 64 },
  },
  {
    top: 1146,
    taskNameSrc: taskName3Svg,
    taskNameAlt: 'Answer a quiz question',
    taskNameLayout: { left: 37.4, top: 68.53, width: 389, height: 44 },
    rewardValueSrc: rewardValue3Svg,
    rewardValueAlt: '+1',
    rewardValueLayout: { left: 548.731, top: 61.84, width: 51, height: 64 },
  },
  {
    top: 1349,
    taskNameSrc: taskName4Svg,
    taskNameAlt: 'Subscribe to Uletnaya Dostavka on VK',
    taskNameLayout: { left: 35.8, top: 44.74, width: 351, height: 91 },
    rewardValueSrc: rewardValue4Svg,
    rewardValueAlt: '+1',
    rewardValueLayout: { left: 548.731, top: 61.84, width: 51, height: 64 },
  },
];

/** Whether the task at list index is done and should be inactive. */
export function isTaskListItemCompleted(
  taskIndex: number,
  playerEconomy: PlayerEconomyState,
  nowMs = Date.now(),
): boolean {
  if (taskIndex === TASK_INDEX_NOTIFICATIONS) {
    return isNotificationsRewardClaimed(playerEconomy);
  }
  if (taskIndex === TASK_INDEX_DAILY_LOGIN) {
    return (
      isDailyLoginStreakCompleted(playerEconomy) ||
      isDailyLoginRewardClaimedToday(playerEconomy, nowMs)
    );
  }
  if (taskIndex === TASK_INDEX_QUIZ) {
    return (
      isQuizComplete(playerEconomy) ||
      isQuizDailyLimitReached(playerEconomy, nowMs)
    );
  }
  if (taskIndex === TASK_INDEX_VK_SUBSCRIBE) {
    return isVkSubscribeRewardClaimed(playerEconomy);
  }
  return false;
}

export function buildTaskCards(
  playerEconomy: PlayerEconomyState,
  nowMs = Date.now(),
): TaskCardModel[] {
  return TASK_TEMPLATES.map((template, index) => ({
    ...template,
    completed: isTaskListItemCompleted(index, playerEconomy, nowMs),
  }));
}

export type TaskListScreenModel = InfoPopupState & {
  tasks: readonly TaskCardModel[];
  onNavigate?: (direction: ArrowDirection) => void;
  onTaskClick?: (taskIndex: number) => void;
  onInfoClick?: () => void;
  onGoBack?: () => void;
};

export function createTaskListScreenModel(
  props: TaskListScreenProps,
  state: TicketsScreenState,
): TaskListScreenModel {
  return {
    tasks: buildTaskCards(props.playerEconomy),
    infoOpen: state.infoOpen,
    setInfoOpen: state.setInfoOpen,
    onInfoClick: props.onInfoClick,
    onNavigate: props.onNavigate,
    onTaskClick: props.onTaskClick,
    onGoBack: props.onGoBack,
  };
}
